import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import ClientService from "../services/ClientService";
import DashboardService from "../../Admin/services/DashboardService";
import AuthService from "../../Admin/services/AuthService";
import { normalizeClient } from "../services/superAdminApiUtils";
import { SaPageShell, SaPageHeader, SaPrimaryButton, SA } from "./shared/SuperAdminUi";

/* ── palette & helpers ──────────────────────────────────────────── */
const GRADIENTS = ["#5e72e4","#2dce89","#fb6340","#f5365c","#11cdef","#344767","#825ee4","#00875a"];
const pickColor = (str = "") => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return GRADIENTS[h % GRADIENTS.length];
};
const initials = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "?";

const today = () => new Date().toISOString().split("T")[0];
const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split("T")[0];
};

const fmt = (n = 0) => n.toLocaleString("en-IN");
const pct = (n = 0) => `${Number(n).toFixed(1)}%`;

function normalizeStatRow(s) {
  return {
    clientId: s.clientId ?? s.ClientId,
    agencyName: s.agencyName ?? s.AgencyName ?? "",
    city: s.city ?? s.City ?? "",
    country: s.country ?? s.Country ?? "",
    totalLeads: s.totalLeads ?? s.TotalLeads ?? 0,
    assigned: s.assigned ?? s.Assigned ?? 0,
    hotClients: s.hotClients ?? s.HotClients ?? 0,
    interested: s.interested ?? s.Interested ?? 0,
    visited: s.visited ?? s.Visited ?? 0,
    dealDone: s.dealDone ?? s.DealDone ?? 0,
    employees: s.employees ?? s.Employees ?? 0,
    conversionRate: s.conversionRate ?? s.ConversionRate ?? 0,
  };
}

/* ── CSS ─────────────────────────────────────────────────────────── */
const CSS = `
.sa-db-table { width:100%; border-collapse:collapse; }
.sa-db-table thead tr { background:linear-gradient(135deg,#344767 0%,#21243d 100%); }
.sa-db-table thead th {
  padding:13px 14px; font-size:10px; font-weight:800; color:rgba(255,255,255,0.75);
  text-transform:uppercase; letter-spacing:.06em; white-space:nowrap; border:none;
}
.sa-db-table tbody tr { border-bottom:1px solid #f0f2f5; transition:background .15s; cursor:pointer; }
.sa-db-table tbody tr:hover { background:#f0f4ff; }
.sa-db-table tbody td { padding:11px 14px; font-size:13px; color:#344767; vertical-align:middle; }
.sa-db-agency { display:flex; align-items:center; gap:10px; }
.sa-db-avatar {
  width:36px; height:36px; border-radius:10px; flex-shrink:0;
  display:flex; align-items:center; justify-content:center;
  font-size:13px; font-weight:800; color:#fff;
}
.sa-db-num { font-weight:700; color:#344767; }
.sa-db-zero { color:#c4cad4; }
.sa-db-conv-bar { background:#f0f2f5; border-radius:20px; height:6px; width:80px; overflow:hidden; display:inline-block; margin-left:6px; vertical-align:middle; }
.sa-db-conv-fill { height:100%; border-radius:20px; background:linear-gradient(90deg,#2dce89,#11cdef); }
.sa-db-badge { display:inline-flex; align-items:center; gap:4px; padding:3px 9px; border-radius:20px; font-size:11px; font-weight:700; }
.sa-db-btn { border:1.5px solid #e9ecef; background:#fff; border-radius:9px; width:32px; height:32px; cursor:pointer; color:#5e72e4; display:inline-flex; align-items:center; justify-content:center; font-size:12px; transition:background .15s; }
.sa-db-btn:hover { background:#5e72e4; color:#fff; border-color:#5e72e4; }
`;

/* ── stat card ──────────────────────────────────────────────────── */
function StatCard({ icon, label, value, color, sub }) {
  return (
    <div style={{ background:"#fff", border:`1px solid ${SA.border}`, borderRadius:"16px", padding:"18px 20px", display:"flex", alignItems:"center", gap:"14px", boxShadow:"0 2px 12px rgba(52,71,103,0.07)", flex:"1 1 160px", minWidth:"140px" }}>
      <div style={{ width:"46px", height:"46px", borderRadius:"12px", background:`${color}18`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"20px", color, flexShrink:0 }}>
        <i className={icon} />
      </div>
      <div>
        <p style={{ margin:0, fontSize:"24px", fontWeight:"800", color:SA.inkDeep, lineHeight:1.1 }}>{value}</p>
        <p style={{ margin:0, fontSize:"11px", color:SA.muted, fontWeight:"600", textTransform:"uppercase", letterSpacing:"0.04em" }}>{label}</p>
        {sub && <p style={{ margin:"3px 0 0", fontSize:"11px", color }}>{sub}</p>}
      </div>
    </div>
  );
}

/* ── section heading ────────────────────────────────────────────── */
function SectionHeading({ icon, title, right }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:"10px", padding:"16px 20px", borderBottom:`1px solid ${SA.border}` }}>
      <i className={icon} style={{ color:SA.primary, fontSize:"15px" }} />
      <span style={{ fontWeight:"800", fontSize:"15px", color:SA.ink, flex:1 }}>{title}</span>
      {right}
    </div>
  );
}

/* ── main component ─────────────────────────────────────────────── */
function SuperAdminDashboard() {
  const navigate = useNavigate();
  const toast = useRef(null);
  const userId = AuthService.getLoggedInUserId();

  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(today());
  const [search, setSearch] = useState("");

  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState([]); // SuperAdminClientStatsDto[]
  const [loading, setLoading] = useState(true);

  /* selected client for quick-view panel */
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [clientCards, setClientCards] = useState(null);
  const [clientLoading, setClientLoading] = useState(false);

  /* ── fetch overview ── */
  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const [cls, ov] = await Promise.all([
        ClientService.getAllCLients(),
        DashboardService.getSuperAdminOverview(fromDate, toDate),
      ]);
      setClients(Array.isArray(cls) ? cls.map((c) => normalizeClient(c) ?? c) : []);
      setStats((Array.isArray(ov) ? ov : []).map(normalizeStatRow));
    } catch {
      toast.current?.show({ severity:"error", summary:"Error", detail:"Failed to load dashboard data.", life:4000 });
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  /* ── quick-view a client ── */
  const selectClient = async (clientId) => {
    if (selectedClientId === clientId) { setSelectedClientId(null); setClientCards(null); return; }
    setSelectedClientId(clientId);
    setClientLoading(true);
    try {
      const cards = await DashboardService.getDashboardCards(userId, clientId, fromDate, toDate);
      setClientCards(cards);
    } catch {
      toast.current?.show({ severity:"error", summary:"Error", detail:"Could not load client data.", life:3000 });
    } finally {
      setClientLoading(false);
    }
  };

  /* ── aggregate totals ── */
  const totals = stats.reduce((acc, s) => ({
    leads:    acc.leads    + s.totalLeads,
    assigned: acc.assigned + s.assigned,
    hot:      acc.hot      + s.hotClients,
    deal:     acc.deal     + s.dealDone,
  }), { leads:0, assigned:0, hot:0, deal:0 });

  const totalConv = totals.assigned > 0 ? (totals.deal / totals.assigned * 100).toFixed(1) : "0.0";

  const filtered = stats.filter((s) =>
    !search || (s.agencyName || "").toLowerCase().includes(search.toLowerCase())
  );

  const selectedClientName = clients.find((c) => c.id === selectedClientId)?.agencyName ?? "";

  return (
    <SaPageShell>
      <style>{CSS}</style>
      <Toast ref={toast} />

      {/* ── page header ── */}
      <SaPageHeader
        title="SuperAdmin Dashboard"
        subtitle="Platform-wide overview across all agency clients."
        actions={
          <SaPrimaryButton iconClass="fas fa-building" onClick={() => navigate("/client")}>
            Manage agencies
          </SaPrimaryButton>
        }
      />

      {/* ── date range filter ── */}
      <div style={{ display:"flex", alignItems:"center", flexWrap:"wrap", gap:"12px", marginBottom:"24px", background:"#fff", padding:"14px 18px", borderRadius:"14px", border:`1px solid ${SA.border}`, boxShadow:"0 2px 8px rgba(52,71,103,0.05)" }}>
        <i className="fas fa-calendar-alt" style={{ color:SA.primary }} />
        <span style={{ fontSize:"13px", fontWeight:"700", color:SA.muted }}>Period</span>
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
          style={{ border:`1.5px solid ${SA.border}`, borderRadius:"9px", padding:"8px 12px", fontSize:"13px", color:SA.ink, outline:"none" }} />
        <span style={{ color:SA.muted, fontSize:"13px" }}>to</span>
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
          style={{ border:`1.5px solid ${SA.border}`, borderRadius:"9px", padding:"8px 12px", fontSize:"13px", color:SA.ink, outline:"none" }} />
        <SaPrimaryButton iconClass="fas fa-sync-alt" onClick={loadOverview}>Refresh</SaPrimaryButton>
      </div>

      {/* ── aggregate stat cards ── */}
      {!loading && (
        <div style={{ display:"flex", flexWrap:"wrap", gap:"14px", marginBottom:"24px" }}>
          <StatCard icon="fas fa-building" label="Active Clients"    value={clients.length}       color={SA.primary} />
          <StatCard icon="fas fa-users"    label="Total Leads"       value={fmt(totals.leads)}    color="#11cdef" />
          <StatCard icon="fas fa-clipboard-list" label="Assigned"   value={fmt(totals.assigned)} color="#fb6340" />
          <StatCard icon="fas fa-fire"     label="Hot Clients"       value={fmt(totals.hot)}      color="#f5365c" />
          <StatCard icon="fas fa-handshake" label="Deals Done"      value={fmt(totals.deal)}     color="#2dce89" sub={`${totalConv}% conv. rate`} />
        </div>
      )}

      {/* ── loading ── */}
      {loading && (
        <div style={{ textAlign:"center", padding:"80px 0", color:SA.muted }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize:"28px", color:SA.primary, display:"block", marginBottom:"12px" }} />
          Loading platform data…
        </div>
      )}

      {!loading && (
        <div style={{ display:"flex", flexDirection:"column", gap:"20px" }}>

          {/* ── search + per-client table ── */}
          <div style={{ background:"#fff", borderRadius:"18px", boxShadow:SA.shadow, overflow:"hidden" }}>
            <SectionHeading
              icon="fas fa-table"
              title="Per-Client Breakdown"
              right={
                <div style={{ display:"flex", alignItems:"center", gap:"8px", background:SA.bgSoft, border:`1px solid ${SA.border}`, borderRadius:"10px", padding:"7px 12px", maxWidth:"220px" }}>
                  <i className="fas fa-search" style={{ color:SA.muted, fontSize:"12px" }} />
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter clients…"
                    style={{ border:"none", outline:"none", background:"transparent", fontSize:"13px", color:SA.ink, width:"150px" }} />
                </div>
              }
            />
            <div style={{ overflowX:"auto", padding:"0 0 8px" }}>
              <table className="sa-db-table">
                <thead>
                  <tr>
                    {["Agency","Location","Leads","Assigned","Hot","Interested","Visited","Deals","Conv %","Employees",""].map((h) => (
                      <th key={h || "_act"}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={11} style={{ textAlign:"center", padding:"40px", color:SA.muted }}>No clients found.</td></tr>
                  )}
                  {filtered.map((s) => {
                    const color = pickColor(s.agencyName);
                    const convNum = s.assigned > 0 ? (s.dealDone / s.assigned * 100) : 0;
                    const isSelected = selectedClientId === s.clientId;
                    return (
                      <tr key={s.clientId}
                        onClick={() => selectClient(s.clientId)}
                        style={{ background: isSelected ? "#f0f4ff" : undefined }}
                      >
                        <td>
                          <div className="sa-db-agency">
                            <div className="sa-db-avatar" style={{ background:color }}>{initials(s.agencyName)}</div>
                            <span style={{ fontWeight:"700", fontSize:"13px" }}>{s.agencyName}</span>
                          </div>
                        </td>
                        <td>
                          {[s.city, s.country].filter(Boolean).join(", ") || <span style={{ color:SA.muted }}>—</span>}
                        </td>
                        <td><span className={s.totalLeads ? "sa-db-num" : "sa-db-zero"}>{s.totalLeads}</span></td>
                        <td><span className={s.assigned ? "sa-db-num" : "sa-db-zero"}>{s.assigned}</span></td>
                        <td>
                          {s.hotClients > 0
                            ? <span className="sa-db-badge" style={{ background:"#fff0f3", color:"#f5365c" }}><i className="fas fa-fire" />{s.hotClients}</span>
                            : <span className="sa-db-zero">0</span>}
                        </td>
                        <td><span className={s.interested ? "sa-db-num" : "sa-db-zero"}>{s.interested}</span></td>
                        <td><span className={s.visited ? "sa-db-num" : "sa-db-zero"}>{s.visited}</span></td>
                        <td>
                          {s.dealDone > 0
                            ? <span className="sa-db-badge" style={{ background:"#e6faf3", color:"#2dce89" }}><i className="fas fa-handshake" />{s.dealDone}</span>
                            : <span className="sa-db-zero">0</span>}
                        </td>
                        <td>
                          <span style={{ fontWeight:"700", color: convNum >= 10 ? "#2dce89" : convNum >= 5 ? "#fb6340" : SA.muted }}>
                            {pct(convNum)}
                          </span>
                          <span className="sa-db-conv-bar">
                            <span className="sa-db-conv-fill" style={{ width:`${Math.min(convNum, 100)}%` }} />
                          </span>
                        </td>
                        <td><span className={s.employees ? "sa-db-num" : "sa-db-zero"}>{s.employees}</span></td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <button className="sa-db-btn" title="Manage client" onClick={() => navigate(`/editClient/${s.clientId}`)}>
                            <i className="fas fa-pen" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── quick-view panel for selected client ── */}
          {selectedClientId && (
            <div style={{ background:"#fff", borderRadius:"18px", boxShadow:SA.shadow, overflow:"hidden" }}>
              <SectionHeading
                icon="fas fa-chart-bar"
                title={`Quick View — ${selectedClientName}`}
                right={
                  <div style={{ display:"flex", gap:"10px" }}>
                    <button onClick={() => { setSelectedClientId(null); setClientCards(null); }}
                      style={{ background:"transparent", border:`1.5px solid ${SA.border}`, borderRadius:"9px", padding:"6px 14px", fontSize:"12px", fontWeight:"700", color:SA.muted, cursor:"pointer" }}>
                      Close
                    </button>
                    <SaPrimaryButton iconClass="fas fa-external-link-alt" onClick={() => navigate(`/editClient/${selectedClientId}`)}>
                      Manage
                    </SaPrimaryButton>
                  </div>
                }
              />
              <div style={{ padding:"20px" }}>
                {clientLoading && (
                  <div style={{ textAlign:"center", padding:"32px", color:SA.muted }}>
                    <i className="fas fa-spinner fa-spin" style={{ color:SA.primary, fontSize:"22px" }} />
                  </div>
                )}
                {!clientLoading && clientCards && (
                  <div style={{ display:"flex", flexWrap:"wrap", gap:"12px" }}>
                    {[
                      { label:"Total Leads",   value: clientCards.leadCount,                icon:"fas fa-users",           color:SA.primary },
                      { label:"Assigned",      value: clientCards.leadAssignedCount,         icon:"fas fa-clipboard-list",  color:"#fb6340" },
                      { label:"Hot Clients",   value: clientCards.hotClientCount,            icon:"fas fa-fire",            color:"#f5365c" },
                      { label:"Interested",    value: clientCards.interestedCount,           icon:"fas fa-star",            color:"#ffd600" },
                      { label:"Upcoming Visit",value: clientCards.upcomingVisitCount,        icon:"fas fa-calendar-check",  color:"#344767" },
                      { label:"Visited",       value: clientCards.visitedCount,              icon:"fas fa-shoe-prints",     color:"#5e72e4" },
                      { label:"Phone N/P",     value: clientCards.phoneNotPickedCount,       icon:"fas fa-phone-slash",     color:"#8392ab" },
                      { label:"Call Back",     value: clientCards.callBackLaterCount,        icon:"fas fa-phone-volume",    color:"#11cdef" },
                    ].map(({ label, value, icon, color }) => (
                      <div key={label} style={{ display:"flex", alignItems:"center", gap:"10px", background:SA.bgSoft, border:`1px solid ${SA.border}`, borderRadius:"12px", padding:"12px 16px", minWidth:"160px" }}>
                        <div style={{ width:"34px", height:"34px", borderRadius:"9px", background:`${color}18`, display:"flex", alignItems:"center", justifyContent:"center", color, fontSize:"14px" }}>
                          <i className={icon} />
                        </div>
                        <div>
                          <p style={{ margin:0, fontSize:"18px", fontWeight:"800", color:SA.inkDeep }}>{value ?? 0}</p>
                          <p style={{ margin:0, fontSize:"11px", color:SA.muted, fontWeight:"600", textTransform:"uppercase", letterSpacing:"0.04em" }}>{label}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </SaPageShell>
  );
}

export default SuperAdminDashboard;
